import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import NoPhotographyIcon from '@mui/icons-material/NoPhotography'
import { useZxing } from 'react-zxing'
import Viewfinder from './Viewfinder'

/**
 * Surface caméra plein cadre + viseur.
 *
 * La vidéo occupe 100% du conteneur en `object-fit: cover`, ce qui garantit
 * un rendu correct quel que soit le rapport d'aspect de l'appareil (4:3 du
 * capteur vs 19.5:9 ou 20:9 de l'écran) sans jamais déformer l'image.
 *
 * Le contrôle de la torche et le changement de caméra passent par le flux
 * de `useZxing` lui-même : auparavant `useMediaStream` ouvrait un second
 * (puis un troisième, via TorchButton) `getUserMedia` sur le même capteur,
 * ce qui consommait de la batterie et pilotait un flux qui n'était pas
 * celui affiché à l'écran.
 *
 * `children` est une render-prop qui reçoit les contrôles disponibles, afin
 * que chaque écran dispose ses boutons comme il l'entend.
 */
export const CameraScanner = ({
  onScan,
  accentColor = '#E84E1B',
  active = true,
  children,
}) => {
  const [facingMode, setFacingMode] = useState('environment')
  const [cameraError, setCameraError] = useState(null)
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false)

  const constraints = useMemo(
    () => ({ audio: false, video: { facingMode } }),
    [facingMode]
  )

  const handleError = useCallback((error) => {
    setCameraError(error?.name === 'NotAllowedError' ? 'denied' : 'unavailable')
  }, [])

  const handleResult = useCallback(
    (result) => {
      setCameraError(null)
      if (onScan) onScan(result)
    },
    [onScan]
  )

  const { ref, torch } = useZxing({
    constraints,
    paused: !active,
    onDecodeResult: handleResult,
    onError: handleError,
  })

  // On n'affiche le bouton de bascule que si l'appareil a réellement
  // plusieurs caméras (les tablettes de billetterie n'en ont souvent qu'une).
  useEffect(() => {
    let cancelled = false
    if (!navigator.mediaDevices?.enumerateDevices) return undefined
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (cancelled) return
        const cameras = devices.filter((d) => d.kind === 'videoinput')
        setHasMultipleCameras(cameras.length > 1)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const switchCamera = useCallback(() => {
    setFacingMode((current) => (current === 'environment' ? 'user' : 'environment'))
  }, [])

  const controls = {
    torch: {
      available: torch.status !== 'unavailable',
      isOn: torch.status === 'on',
      toggle: () => (torch.status === 'on' ? torch.off() : torch.on()),
    },
    camera: { canSwitch: hasMultipleCameras, switch: switchCamera, facingMode },
    // Permet à l'écran hôte de masquer ses consignes quand il n'y a pas d'image.
    error: cameraError,
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: '#06070A',
        overflow: 'hidden',
      }}
    >
      <Box
        component="video"
        ref={ref}
        // playsInline est indispensable sur iOS : sans lui, Safari bascule la
        // vidéo en lecteur plein écran natif et le viseur devient inutilisable.
        playsInline
        muted
        autoPlay
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          // Effet miroir en caméra frontale, comme attendu par l'utilisateur
          transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
        }}
      />

      {cameraError ? (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ position: 'absolute', inset: 0, p: 4, textAlign: 'center', bgcolor: '#06070A' }}
        >
          <NoPhotographyIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h6">
            {cameraError === 'denied' ? 'Accès à la caméra refusé' : 'Caméra indisponible'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 320 }}>
            {cameraError === 'denied'
              ? "Autorisez l'accès à la caméra dans les réglages de votre navigateur, puis réessayez."
              : "Aucune caméra n'a pu être ouverte sur cet appareil."}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Réessayer
          </Button>
        </Stack>
      ) : (
        <Viewfinder color={accentColor} active={active} />
      )}

      {typeof children === 'function' ? children(controls) : children}
    </Box>
  )
}

export default CameraScanner
